import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Container,
  Divider,
  Form,
  Grid,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "../../app/layout/PageHeader";
import { useStore } from "../../app/stores/store";
import LoadingComponent from "../../app/layout/LoadingComponent";
import { TicketItemType } from "../../app/models/enums";
import PartItemForm from "../tickets/dashboard/modals/PartItemForm";
import { SavePurchaseFormValues } from "../../app/models/inventory";
import MyDropdownInput from "../../app/common/form/MyDropdownInput";
import { v4 as uuid } from 'uuid';
import InventoryPurchasesTable from "./InventoryPurchasesTable";
import * as Yup from "yup";
import MyIconTextInput from "../../app/common/form/MyIconTextInput";

export default observer(function BulkInventoryPurchases() {
  const { t } = useTranslation(["common", "inventory"]);
  const { modalStore, inventoryIndexStore, inventoryPurchasesStore, vendorStore } = useStore();
  const {
    getVendor,
    loadVendors,
    loadDropdownListVendors,
    vendorDropdownList
  } = vendorStore;
  const {
    inventoryStockLocationList,
    loadDropdownListStockLocations,
  } = inventoryIndexStore;
  const {
    loadingInitial,
    loadPurchases,
    resetPurchasesRegistry,
    getPurchases,
    setPurchasesPredicate,
    setPurchasesItem,
    purchasesPredicate,
    updatePurchases,
    resetPredicate,
  } = inventoryPurchasesStore;

  const [vendorId, setVendorId] = useState<string | undefined>(purchasesPredicate.get('vendorId') ?? undefined);
  const [loadCounter, setLoadCounter] = useState<number>(0);

  const validationSchema = Yup.object({});
  
  const navigate = useNavigate();

  useEffect(
    () => {
      loadDropdownListStockLocations();
    },
    [loadDropdownListStockLocations]
  )

  useEffect(
    () => {
      loadVendors()
    }, [loadVendors]
  )

  useEffect(
    () => {
      if (!vendorDropdownList || !vendorDropdownList.length)
        loadDropdownListVendors();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadDropdownListVendors]
  )

  useEffect(() => {
    if (vendorId) {
      setPurchasesPredicate('vendorId', vendorId);
      loadPurchases();
      setLoadCounter(loadCounter + 1);
    }
    else {
      resetPurchasesRegistry();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const newPurchaseInitialValues = useMemo(() => {
    let initialValues = new SavePurchaseFormValues();
    initialValues.vendorId = vendorId;
    if(vendorId) {
      const vendor = getVendor(vendorId);
      initialValues.partMarkup = vendor?.defaultMarkup;
    }
    return initialValues;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId])

  return (
    <>
      <Formik
        validationSchema={validationSchema}
        validateOnBlur={false}
        validateOnChange={false}

        initialValues={{
          purchases: (getPurchases ?? []).map((purchase) => ({
            id: purchase.id,
            unitCost: purchase.unitCost ?? 0,
            quantityPurchased: 0,
            locationId: purchase.locationId
          } as SavePurchaseFormValues)),
          vendorId: undefined,
          poNumber: undefined
        }}
        onSubmit={(values, { setSubmitting, setFieldValue }) => {
          setSubmitting(false);
          updatePurchases(values.purchases.filter(p => p.isDirty).map(p => ({ ...p, vendorId: p.vendorId ?? vendorId })), values.poNumber ?? "")
            .then(() => {
              resetPredicate();
              navigate('/inventoryIndex')
            })
        }}
      >
        {({ handleSubmit, values, isSubmitting, setFieldValue }) => (
          <Form className="ui form error" autoComplete="off" onSubmit={handleSubmit}>
            <Grid stackable columns={4}>

              <Grid.Column>
                <PageHeader
                  header={t("New Purchase", { ns: "common" })}
                  type="h1"
                  divider={false}
                  getAlerts={true}
                  addTitle={true}
                />
              </Grid.Column>
              <Grid.Column textAlign="center">
                <MyDropdownInput
                  options={vendorDropdownList}
                  placeholder={t("pops.vendor", { ns: "tickets" })}
                  name="vendorId"
                  onChangeFunction={(val: string) => setVendorId(val)}
                />
              </Grid.Column>
              <Grid.Column textAlign="center">
                <MyIconTextInput
                  name="poNumber"
                  icon='hashtag'
                  iconPosition="left"
                  placeholder={t("estimate.po", { ns: "tickets" })}
                  maxLength={20}
                />
              </Grid.Column>
              {vendorId && (
              <Grid.Column textAlign="right">
                <Button
                  color="green"
                  icon="plus"
                  content="Add New Part"
                  type="button"
                  onClick={() => {
                    modalStore.openModal(
                      <PartItemForm
                        isAdd={true}
                        forPurchases={true}
                        part={newPurchaseInitialValues}
                        addItem={async (
                          partFormValues: SavePurchaseFormValues,
                          type: TicketItemType
                        ) => {
                          // eslint-disable-next-line eqeqeq
                          var locationName = (inventoryStockLocationList ?? []).find(l => l.value == partFormValues.locationId)?.text?.toLocaleString() ?? undefined;
                          var newPurchaseItem = {
                            ...partFormValues,
                            id: partFormValues.id ?? uuid(),
                            partNumber: partFormValues.partCode!,
                            partDescription: partFormValues.description!,
                            unitCost: Number(partFormValues.unitPrice ?? "0"),
                            quantityPurchased: 0,
                            locationId: partFormValues.locationId,
                            vendorId: partFormValues.vendorId,
                            markup: partFormValues.partMarkup,
                            location: locationName,
                            isDirty: true
                          };

                          setPurchasesItem(newPurchaseItem);
                          setFieldValue('purchases', [...values.purchases, newPurchaseItem]);
                        }}
                        autoCapitalizeFirstLetter={false}
                        vendorDropdownList={vendorDropdownList}
                      />
                    );
                  }}
                  data-testid="add"
                />
              </Grid.Column>
              )}
            </Grid>
            <Divider />
            {
              loadingInitial ? (
                <LoadingComponent
                  content={t("purchases_loading", { ns: "inventory" })}
                />
              )
                : (
                  <Container className="page">
                    <InventoryPurchasesTable
                      loadCounter={loadCounter}
                      values={values}
                      setFieldValue={setFieldValue}
                      setLoadCounter={setLoadCounter}
                    />
                    <Button
                      className="save_button mobile_buttons"
                      loading={isSubmitting || loadingInitial}
                      color="green"
                      content="Save"
                      icon="check"
                      type="submit"
                    />

                    <Link to="/inventoryIndex/">
                      <Button
                        className="save_button mobile_buttons"
                        as={Link}
                        to="/inventoryIndex/"
                        icon="cancel"
                        type="button"
                        content={t("cancel", { ns: "common" })}
                        onClick={() => {
                          resetPredicate();
                        }}
                      />
                    </Link>
                  </Container>
                )}
          </Form>

        )}
      </Formik>
    </>
  );
});
